/**
 * Type de professeur — gouverne quels blocs s'affichent sur la fiche publique.
 *
 * - `creator`    : publie des exercices → fiche complète (badge + section exos + CTA « Travaille avec … »).
 * - `ambassador` : recommande / représente la marque, ne publie pas → pas d'exos, libellé « Ambassadeur », CTA générique.
 * - `user`       : utilise la plateforme avec ses élèves sans publier de contenu public → pas d'exos, CTA générique.
 *
 * Auto-attribué au backfill (migration 0189) : `creator` si ≥ 1 exercice publié,
 * sinon `ambassador`. Ensuite éditable par le prof dans « Mon profil ».
 * Source de vérité de l'énumération (miroir du CHECK SQL `tutor_profiles.tutor_kind`).
 */
export type TutorKind = 'creator' | 'ambassador' | 'user';

export const TUTOR_KINDS: ReadonlyArray<{
  value: TutorKind;
  label: string;
  description: string;
}> = [
  {
    value: 'creator',
    label: 'Créateur',
    description: 'Je publie des exercices sur JeRaisonne.',
  },
  {
    value: 'ambassador',
    label: 'Ambassadeur',
    description: 'Je recommande et représente JeRaisonne, sans y publier d’exercices.',
  },
  {
    value: 'user',
    label: 'Utilisateur',
    description: 'J’utilise JeRaisonne avec mes élèves, sans publier de contenu public.',
  },
] as const;

const KIND_SET = new Set<TutorKind>(TUTOR_KINDS.map((k) => k.value));

/** Normalise une valeur inconnue (row stale / null) vers un TutorKind sûr. */
export function normalizeTutorKind(value: string | null | undefined): TutorKind {
  return value && KIND_SET.has(value as TutorKind) ? (value as TutorKind) : 'creator';
}

export function tutorKindLabel(kind: TutorKind): string {
  return TUTOR_KINDS.find((k) => k.value === kind)?.label ?? 'Créateur';
}

export type TutorProfileDisplay = {
  /** Sur-titre du hero de la fiche publique. */
  heroKicker: string;
  /** Afficher le badge « N exercices publiés » dans le hero. */
  showExerciseBadge: boolean;
  /** Afficher la section « Exercices publiés » (liste). */
  showExercisesSection: boolean;
  /** Libellé du badge de la carte carousel ; `null` = afficher le compteur d'exos. */
  cardBadge: string | null;
  /** Style du CTA de bas de fiche : lié à l'auteur (créateur) ou générique. */
  ctaMode: 'work_with' | 'generic';
};

/**
 * Politique d'affichage de la fiche publique selon le type de professeur.
 * Fonction PURE — testée + partagée entre `apps/marketing` et `apps/app`.
 */
export function tutorProfileDisplay(kind: TutorKind): TutorProfileDisplay {
  switch (kind) {
    case 'ambassador':
      return {
        heroKicker: 'Ambassadeur JeRaisonne',
        showExerciseBadge: false,
        showExercisesSection: false,
        cardBadge: 'Ambassadeur',
        ctaMode: 'generic',
      };
    case 'user':
      return {
        heroKicker: 'Professeur sur JeRaisonne',
        showExerciseBadge: false,
        showExercisesSection: false,
        cardBadge: 'Professeur',
        ctaMode: 'generic',
      };
    case 'creator':
    default:
      return {
        heroKicker: 'Professeur sur JeRaisonne',
        showExerciseBadge: true,
        showExercisesSection: true,
        cardBadge: null,
        ctaMode: 'work_with',
      };
  }
}
